import React, { useState, useEffect } from "react";
import { FileCode, RefreshCw, Clock, AlertTriangle, AlertCircle, ExternalLink } from "lucide-react";
import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from "../config";

/**
 * CodeQuality component displays code quality reports.
 * Shows both backend (Golang) and frontend (ESLint) code quality reports.
 */
export default function CodeQuality() {
  const [codeQualityReport, setCodeQualityReport] = useState(null);
  const [eslintCodeQualityReport, setEslintCodeQualityReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [eslintReportLoading, setEslintReportLoading] = useState(false);
  
  // Fetch both code quality reports when component mounts
  useEffect(() => {
    fetchCodeQualityReport();
    fetchEslintCodeQualityReport();
  }, []);
  
  // Function to fetch the backend code quality report (Golang)
  const fetchCodeQualityReport = async () => {
    try {
      setReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.CODE_QUALITY_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        console.log('Backend code quality report:', response.data);
        setCodeQualityReport(response.data);
      } else {
        console.error('Error fetching backend code quality report:', response.data);
        setCodeQualityReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setReportLoading(false);
    } catch (error) {
      console.error('Error fetching backend code quality report:', error);
      setCodeQualityReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setReportLoading(false);
    }
  };
  
  // Function to fetch the frontend code quality report (ESLint)
  const fetchEslintCodeQualityReport = async () => {
    try {
      setEslintReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.ESLINT_CODE_QUALITY_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        console.log('Frontend code quality report:', response.data);
        setEslintCodeQualityReport(response.data);
      } else {
        console.error('Error fetching frontend code quality report:', response.data);
        setEslintCodeQualityReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setEslintReportLoading(false);
    } catch (error) {
      console.error('Error fetching frontend code quality report:', error);
      setEslintCodeQualityReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setEslintReportLoading(false);
    }
  };
  
  // Helper function to format severity
  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'text-red-600 bg-red-50';
      case 'major':
        return 'text-orange-600 bg-orange-50';
      case 'minor':
        return 'text-yellow-600 bg-yellow-50';
      case 'info':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  
  // Calculate report stats
  const calculateReportStats = (report) => {
    if (!report || !report.report || !Array.isArray(report.report)) {
      return { total: 0, critical: 0, major: 0, minor: 0, info: 0 };
    }
    
    const stats = { total: report.report.length, critical: 0, major: 0, minor: 0, info: 0 };
    
    report.report.forEach(issue => {
      if (issue.severity) {
        switch (issue.severity.toLowerCase()) {
          case 'critical':
            stats.critical++;
            break;
          case 'major':
            stats.major++;
            break;
          case 'minor':
            stats.minor++;
            break;
          case 'info':
            stats.info++;
            break;
          default:
            // No category
            break;
        }
      }
    });
    
    return stats;
  };
  
  // Calculate stats for both reports
  const backendStats = calculateReportStats(codeQualityReport);
  const frontendStats = calculateReportStats(eslintCodeQualityReport);
  
  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold mb-4 text-gray-800">
          Code Quality Reports
        </h1>
      </div>
      
      <div className="space-y-6 overflow-auto pb-4 pr-2">
        {/* Backend Code Quality Report Card */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 flex flex-col overflow-hidden" style={{ height: '480px', maxWidth: '100%' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
                <FileCode size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Backend Code Quality</h2>
                <p className="text-sm text-gray-500">Golang code analysis</p>
              </div>
            </div>
            <button 
              onClick={fetchCodeQualityReport}
              disabled={reportLoading}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              title="Refresh report"
            >
              <RefreshCw size={18} className={reportLoading ? "animate-spin" : ""} />
            </button>
          </div>
          
          {/* Report content */}
          {reportLoading ? (
            <div className="flex justify-center py-8 flex-grow">
              <div className="animate-pulse flex flex-col items-center">
                <div className="rounded-full bg-gray-200 w-12 h-12 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mb-2.5"></div>
                <div className="h-3 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
          ) : !codeQualityReport ? (
            <div className="text-center py-8 text-gray-500 flex-grow">
              No report available
            </div>
          ) : codeQualityReport.status === 'error' ? (
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700 flex-grow">
              <div className="flex mb-2">
                <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                <h3 className="font-medium">Error retrieving report</h3>
              </div>
              <p className="text-sm">{codeQualityReport.message}</p>
            </div>
          ) : (
            <div className="flex flex-col flex-grow min-w-0 overflow-hidden">
              {/* Report stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{backendStats.total}</div>
                  <div className="text-sm text-gray-500">Total Issues</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-600">{backendStats.critical}</div>
                  <div className="text-sm text-red-600">Critical</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-600">{backendStats.major}</div>
                  <div className="text-sm text-orange-600">Major</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-yellow-600">{backendStats.minor}</div>
                  <div className="text-sm text-yellow-600">Minor</div>
                </div>
              </div>
              
              {/* Timestamp */}
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <Clock size={14} className="mr-1" />
                Last updated: {codeQualityReport.timestamp ? new Date(codeQualityReport.timestamp).toLocaleString() : 'Unknown'}
              </div>
              
              {/* List of issues */}
              <div className="border border-gray-100 rounded-lg overflow-hidden flex-grow flex flex-col">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 border-b border-gray-100 sticky top-0">
                  Issues List
                </div>
                <div className="divide-y divide-gray-100 overflow-y-auto overflow-x-auto flex-grow w-full min-w-0">
                  {codeQualityReport.report && codeQualityReport.report.length > 0 ? (
                    codeQualityReport.report.map((issue, index) => (
                      <div key={index} className="px-4 py-3 hover:bg-gray-50">
                        <div className="flex items-center mb-1">
                          <AlertTriangle size={14} className="mr-1 text-yellow-500 flex-shrink-0" />
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityColor(issue.severity)} flex-shrink-0`}>
                            {issue.severity}
                          </span>
                          <span className="text-xs text-gray-500 ml-2 overflow-hidden text-ellipsis">{issue.check_name}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-700">{issue.description}</p>
                        <div className="text-xs text-gray-500 mt-1 flex justify-between">
                          <span>{issue.location.path}:{issue.location.lines.begin}</span>
                          {issue.categories && (
                            <span className="text-xs text-gray-400">
                              {issue.categories.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-center text-gray-500">
                      No issues found
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Frontend Code Quality Report Card */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 flex flex-col overflow-hidden" style={{ height: '480px', maxWidth: '100%' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-50 text-blue-600 mr-4">
                <FileCode size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Frontend Code Quality</h2>
                <p className="text-sm text-gray-500">ESLint analysis</p>
              </div>
            </div>
            <button 
              onClick={fetchEslintCodeQualityReport}
              disabled={eslintReportLoading}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              title="Refresh report"
            >
              <RefreshCw size={18} className={eslintReportLoading ? "animate-spin" : ""} />
            </button>
          </div>
          
          {/* Report content */}
          {eslintReportLoading ? (
            <div className="flex justify-center py-8 flex-grow">
              <div className="animate-pulse flex flex-col items-center">
                <div className="rounded-full bg-gray-200 w-12 h-12 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mb-2.5"></div>
                <div className="h-3 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
          ) : !eslintCodeQualityReport ? (
            <div className="text-center py-8 text-gray-500 flex-grow">
              No report available
            </div>
          ) : eslintCodeQualityReport.status === 'error' ? (
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700 flex-grow">
              <div className="flex mb-2">
                <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                <h3 className="font-medium">Error retrieving report</h3>
              </div>
              <p className="text-sm">{eslintCodeQualityReport.message}</p>
            </div>
          ) : (
            <div className="flex flex-col flex-grow min-w-0 overflow-hidden">
              {/* Report stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{frontendStats.total}</div>
                  <div className="text-sm text-gray-500">Total Issues</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-600">{frontendStats.critical}</div>
                  <div className="text-sm text-red-600">Critical</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-600">{frontendStats.major}</div>
                  <div className="text-sm text-orange-600">Major</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-yellow-600">{frontendStats.minor}</div>
                  <div className="text-sm text-yellow-600">Minor</div>
                </div>
              </div>
              
              {/* Timestamp */}
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <Clock size={14} className="mr-1" />
                Last updated: {eslintCodeQualityReport.timestamp ? new Date(eslintCodeQualityReport.timestamp).toLocaleString() : 'Unknown'}
              </div>
              
              {/* List of issues */}
              <div className="border border-gray-100 rounded-lg overflow-hidden flex-grow flex flex-col">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 border-b border-gray-100 sticky top-0">
                  Issues List
                </div>
                <div className="divide-y divide-gray-100 overflow-y-auto overflow-x-auto flex-grow w-full min-w-0">
                  {eslintCodeQualityReport.report && eslintCodeQualityReport.report.length > 0 ? (
                    eslintCodeQualityReport.report.map((issue, index) => (
                      <div key={index} className="px-4 py-3 hover:bg-gray-50">
                        <div className="flex items-center mb-1">
                          <AlertTriangle size={14} className="mr-1 text-yellow-500 flex-shrink-0" />
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityColor(issue.severity)} flex-shrink-0`}>
                            {issue.severity}
                          </span>
                          <span className="text-xs text-gray-500 ml-2 overflow-hidden text-ellipsis">{issue.check_name}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-700">{issue.description}</p>
                        <div className="text-xs text-gray-500 mt-1 flex justify-between">
                          <span>{issue.location.path}:{issue.location.lines.begin}</span>
                          {issue.categories && (
                            <span className="text-xs text-gray-400">
                              {issue.categories.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-center text-gray-500">
                      No issues found
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
